<template>
  <div
    class="absolute transition-all duration-300"
    :style="{
      top: period.position.top,
      left: period.position.left
    }"
  >
    <div
      id="my-session-slot"
      class="border border-l-4 rounded-lg bg-pure-white p-1 flex flex-col justify-between gap-1 shadow-md transition-all duration-300"
      :style="{
        height: period.element.height,
        width: period.element.width,
        borderColor: period.element.borderColor
      }"
    >
      <div
        class="relative h-full w-full rounded py-1 px-1.5"
        :style="{ backgroundColor: period.secondary_color }"
        @click="detailsOpened = true"
      >
        <div
          class="cursor-pointer h-full flex flex-col justify-center gap-1"
          :title="period.client.name"
        >
          <BlTextSubtitle3
            :text="`${period.client.name}`"
            :style="{ color: period.primary_color }"
            class="truncate"
          />
          <BlTextBody2
            v-if="isHasThreeSpaces"
            :text="`${period.start_time_string} - ${period.end_time_string}`"
            :style="{ color: period.primary_color }"
          />
          <BlTextSubtitle3
            v-if="period.room && isHasTwoSpaces"
            :text="`${period.room.name}`"
            :style="{ color: period.primary_color }"
            class="truncate"
          />
          <BlTextBody2
            v-if="enableBranch && period.room.branch && isHasTwoSpaces"
            :text="period.room.branch.name"
            :style="{ color: period.primary_color }"
            class="truncate"
          />
        </div>

        <div
          v-if="detailsOpened"
          :data-id="dataId"
          class="absolute top-0 flex flex-col gap-1 mx-2 w-64 flex-shrink-0 bg-pure-white rounded border border-grey-200 overflow-y-auto shadow-md"
          :class="[period.is_last_column ? 'right-full' : 'left-full']"
          :style="{ zIndex: 2 }"
        >
          <div
            :data-id="dataId"
            class="w-full h-8 flex-shrink-0 flex items-center justify-between px-3"
            :class="{
              'bg-orange-200': period.status === 'scheduled',
              'bg-cornflower-200': period.status === 'in_progress',
              'bg-lime-200': period.status === 'completed',
              'bg-error-100': period.status === 'cancelled'
            }"
          >
            <BlTextBody3
              :data-id="dataId"
              :text="period.status.replaceAll('_', ' ')"
              class="capitalize"
              :class="{
                'text-orange': period.status === 'scheduled',
                'text-cornflower-800': period.status === 'in_progress',
                'text-lime': period.status === 'completed',
                'text-error': period.status === 'cancelled'
              }"
            />
            <BlIcon
              icon="close"
              :color="
                period.status === 'scheduled'
                  ? 'orange'
                  : period.status === 'in_progress'
                  ? 'cornflower-8'
                  : period.status === 'completed'
                  ? 'success'
                  : 'error'
              "
              class="h-4 w-4"
              clickable
            />
          </div>
          <div :data-id="dataId" class="p-3 flex-shrink-0 flex flex-col gap-3">
            <div :data-id="dataId" class="flex flex-col gap-1">
              <div class="flex" :title="period.client.name">
                <BlTextSubtitle3
                  :data-id="dataId"
                  :text="period.client.name"
                  class="truncate"
                />
              </div>
              <BlTextBody2 :data-id="dataId" :text="displayDate(period.date)" />
              <BlTextBody2
                :data-id="dataId"
                :text="
                  `${period.start_time_string} - ${period.end_time_string}`
                "
              />
            </div>
            <div :data-id="dataId" class="flex flex-col items-start gap-1">
              <div
                v-if="enableBranch && period.room.branch"
                :data-id="dataId"
                class="h-7 max-w-54 px-2 flex items-center justify-center rounded"
                :style="{
                  backgroundColor:
                    period.room.branch.background_color || 'unset'
                }"
              >
                <BlTextBody2
                  :data-id="dataId"
                  class="truncate"
                  :text="period.room.branch.name || '-'"
                  :style="{ color: period.room.branch.color || 'unset' }"
                />
              </div>
              <div
                v-if="period.room"
                :data-id="dataId"
                class="max-w-54 flex"
                :title="period.room.name"
              >
                <BlTextBody2
                  :data-id="dataId"
                  :text="period.room.name"
                  class="truncate"
                />
              </div>
              <div :data-id="dataId" class="max-w-54 flex">
                <BlTextBody2
                  v-if="!period.user"
                  :data-id="dataId"
                  title="Deleted user"
                  text="Deleted user"
                  class="italic truncate"
                />
                <BlTextBody2
                  v-else
                  :data-id="dataId"
                  :title="period.user.name"
                  :text="period.user.name"
                  class="truncate"
                />
              </div>
            </div>
          </div>
          <div
            class="h-12 w-full flex-shrink-0 border-t border-slate-400 flex items-center justify-center gap-14 px-3"
          >
            <router-link
              v-if="period.status === 'scheduled'"
              class="cursor-pointer"
              title="View session draft"
              :to="`/session/${period.session.slug}/edit`"
            >
              <BlTextSubtitle3
                :data-id="dataId"
                text="View session draft"
                class="text-light-purple"
              />
            </router-link>
            <router-link
              v-if="period.status === 'in_progress'"
              class="cursor-pointer"
              title="Resume session"
              :to="`/session/${period.session.slug}`"
            >
              <BlTextSubtitle3
                :data-id="dataId"
                text="Resume session"
                class="text-light-purple"
              />
            </router-link>
            <router-link
              v-if="
                period.status === 'completed' || period.status === 'cancelled'
              "
              class="cursor-pointer"
              title="View session"
              :to="`/session/${period.session.slug}`"
            >
              <BlTextSubtitle3
                :data-id="dataId"
                text="View session"
                class="text-light-purple"
              />
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlIcon from "../../BlIcon.vue";

export default {
  name: "MySessionPeriod",
  components: {
    BlTextSubtitle3,
    BlTextBody2,
    BlTextBody3,
    BlIcon
  },
  props: {
    isCompact: { type: Boolean, default: false },
    period: { type: Object, required: true }
  },
  data() {
    return {
      detailsOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    dataId() {
      const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      let combination = "";
      for (let i = 0; i < 6; i++) {
        combination += chars[Math.round(Math.random() * 35)];
      }
      return `bl-appointment-details-${combination}`;
    },
    isHasTwoSpaces() {
      const slot = this.getSlotCount(
        this.period.start_time_string,
        this.period.end_time_string
      );
      return !this.isCompact || slot > 1;
    },
    isHasThreeSpaces() {
      const slot = this.getSlotCount(
        this.period.start_time_string,
        this.period.end_time_string
      );
      return !this.isCompact || slot > 2;
    }
  },
  watch: {
    detailsOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  mounted() {},
  methods: {
    closeHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.dataId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closeHandler);
        this.detailsOpened = false;
      }
    },
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    displayDate(date) {
      return moment(date).format("dddd, DD MMM YYYY");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/mySessions/timeslot/Slot.vue